import React, { useCallback, useEffect, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { useIntl } from 'react-intl';

import CartError from 'components/cart-error';
import CartLoading from 'components/cart-loading';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useCart } from 'hooks/use-cart';
import useDebounceEffect from 'hooks/use-debounce-effect';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useMinimalCart } from 'hooks/use-minimal-cart';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import { logCheckoutEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/menu/main-menu';
import { OrderStatus } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import CartContent from './cart-content';
import { useDeliveryErrorModalCopy } from './hooks/use-delivery-error-modal-copy';
import { IErrorsListItems } from './types';

export const Cart = () => {
  const { cartEntries, calculateCartTotalWithDiscount } = useCart();
  const {
    serverOrder,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    priceOrderFailure,
    priceMutationError,
    orderStatus,
    cardSuccessfullyAdded,
    tryPricing,
    serviceMode,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
  } = useCartContext();

  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { sections: addOnSections } = useFeatureMenuAddOn();
  // Retrieve payment processor to determine which payment page to display
  const { checkoutPaymentMethod } = usePaymentContext();
  const enableSignupAfterCart = useFlag(LaunchDarklyFlag.ENABLE_MOVE_SIGNUP_AFTER_CART);
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const { getStoreMenu, storeMenuLoading, storeMenuCalled } = useMainMenuContext();
  const { region } = useLocale();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const dismissAndReplace = useDismissAndReplace();

  const storeId = store.number || '';

  const {
    isRestaurantPosOnline,
    loading: isRestaurantPosConnectivityStatusLoading,
  } = useRestaurantPosConnectivityStatus({
    storeId,
  });

  const [hydrationStatus, setHydrationStatus] = useState({
    processed: false,
    executing: false,
    error: false,
  });
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { hydrateMinimalCart } = useMinimalCart();

  useEffect(() => {
    if (isUnifiedMenuEnabled && minimalCartEntries.length && !hydrationStatus.processed) {
      setHydrationStatus(status => ({ ...status, processed: true, executing: true }));
      hydrateMinimalCart().then((success: boolean) => {
        if (!success) {
          setShouldSkipPriceOrder(false);
        }
        return setHydrationStatus(status => ({ ...status, executing: false, error: !success }));
      });
    } else {
      setHydrationStatus(status => ({ ...status, processed: true }));
    }
  }, [
    hydrateMinimalCart,
    hydrationStatus.processed,
    isUnifiedMenuEnabled,
    minimalCartEntries.length,
    setShouldSkipPriceOrder,
  ]);

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const isMobile = useIsMobileBreakpoint();
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { isCartTotalNegative } = calculateCartTotalWithDiscount();
  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const isPriceLoading = isOrderPriceLoading || evaluatingIncentives;
  const hasPriceError = !!priceMutationError || priceOrderFailure || hydrationStatus.error;

  const showCartErrorModal =
    hasPriceError &&
    !unavailableCartEntries.length &&
    !isCartTotalNegative &&
    !shouldSkipPriceOrder;
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const errorsList: IErrorsListItems = [
    { priceMutationError },
    { priceOrderFailure },
    { unavailableCartItems },
    { isRestaurantPosOnline: !isRestaurantPosOnline },
    { isStoreOpenAndAvailable: !isStoreOpenAndAvailable },
  ];
  const { errorModalBody, errorModalTitle } = useDeliveryErrorModalCopy(serverOrder, errorsList);

  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);
  const [loadingAnimationComplete, setLoadingAnimationComplete] = useState(false);

  // Don't redirect to SignIn/SignUp if:
  // - The flag enable-signup-after-cart is enabled
  useAuthRedirects({ skip: enableSignupAfterCart });

  useEffectOnce(() => {
    if (isStoreOpenAndAvailable && serviceMode) {
      logCheckoutEvent(serviceMode, cartEntries);
    }
  });

  useDebounceEffect(
    () => {
      if (!isStoreOpenAndAvailable || !serviceMode) {
        return;
      }
      shouldSkipPriceOrder && setShouldSkipPriceOrder(false);
      tryPricing();
    },
    250,
    // Changes on rewards should trigger a reprice. That side effect is not captured within the other deps.
    // This usually triggers a second PriceOrder, because the first attempt usually have null rewards
    // until the Redux slice sync-up with the LocalStorage (only happens on page refresh web).
    [appliedLoyaltyRewardsArray, isStoreOpenAndAvailable, serviceMode, tryPricing]
  );

  useEffect(() => {
    if (storeId) {
      getStoreMenu({ region, storeId });
    }
  }, [getStoreMenu, region, storeId]);

  useEffect(() => {
    setShowUnavailableItemsModal(!isPriceLoading && unavailableCartItems);
  }, [isPriceLoading, unavailableCartItems]);

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  const handleDismissCartErrorModal = () => setShouldSkipPriceOrder(true);

  // to allow below dismissAndReplace to run with expo router which can't navigate on first render if cart deep link was used
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Redirect to Menu if:
  // - Cart is empty
  // - Selected offer is null
  // - The order has not been inserted or has been requested
  // - Hydration was completed successfully
  // Or
  // - Service mode is null
  const hydrationSuccessful =
    hydrationStatus.processed && !hydrationStatus.executing && !hydrationStatus.error;

  const shouldRedirectToMenu =
    (hydrationSuccessful &&
      !cartEntries.length &&
      !OrderSuccessFailureStatuses.success.pos.includes(orderStatus as OrderStatus) &&
      !OrderSuccessFailureStatuses.inProgress.pos.includes(orderStatus as OrderStatus)) ||
    (!serviceMode && !orderServiceMode);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (shouldRedirectToMenu) {
      dismissAndReplace(routes.menu);
    }
  }, [dismissAndReplace, shouldRedirectToMenu, isMounted]);

  if (!isRestaurantPosOnline && !shouldSkipPriceOrder) {
    return (
      <CartError
        startOver
        body={errorModalBody}
        title={errorModalTitle}
        onDismiss={handleDismissCartErrorModal}
        additionalLoggingContext={{
          isRestaurantPosOnline,
          shouldSkipPriceOrder,
        }}
      />
    );
  }

  // Show the loading cart if:
  // - User is authenticated or flag enable-signup-after-cart is disabled
  // - Is restaurant online or is pricing or is getting rewards
  // - Is executing hydration
  // - Is the Store Menu Data loading or is menu service data disabled
  const isHydrationPending = !hydrationStatus.processed || hydrationStatus.executing;
  const loadingLegacyMenu = !isUnifiedMenuEnabled && (!storeMenuCalled || storeMenuLoading);
  const dataRelatedIsLoading =
    !isCartTotalNegative &&
    (isAuthenticated || !enableSignupAfterCart) &&
    (isRestaurantPosConnectivityStatusLoading || isHydrationPending || loadingLegacyMenu);

  if (dataRelatedIsLoading || !loadingAnimationComplete) {
    return (
      // do not remove below fragment. it prevents a double mount of the <CardModal> and associated animations
      <>
        <CartLoading
          setLoadingAnimationComplete={setLoadingAnimationComplete}
          dataRelatedIsLoading={dataRelatedIsLoading}
        />
      </>
    );
  }

  if (isMobile && (showCartErrorModal || showUnavailableItemsModal)) {
    if (showUnavailableItemsModal) {
      return (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleUnavailableModalDismiss}
          unavailableCartEntries={unavailableCartEntries}
          isInCart
        />
      );
    }
    return (
      <CartError
        startOver
        body={errorModalBody}
        title={errorModalTitle}
        onDismiss={handleDismissCartErrorModal}
        additionalLoggingContext={{
          priceMutationError,
          priceOrderFailure,
          unavailableCartEntriesLength: unavailableCartEntries.length,
          isCartTotalNegative,
          shouldSkipPriceOrder,
          hydrationError: hydrationStatus.error,
        }}
      />
    );
  }

  if (showCartErrorModal) {
    return (
      <CartError
        startOver
        body={errorModalBody}
        title={errorModalTitle}
        onDismiss={handleDismissCartErrorModal}
        additionalLoggingContext={{
          priceMutationError,
          priceOrderFailure,
          unavailableCartEntriesLength: unavailableCartEntries.length,
          isCartTotalNegative,
          shouldSkipPriceOrder,
          hydrationError: hydrationStatus.error,
        }}
      />
    );
  }

  if (showUnavailableItemsModal) {
    return (
      <RemoveUnavailableItemsModal
        isInCart
        unavailableCartEntries={unavailableCartEntries}
        onConfirmCb={handleUnavailableModalDismiss}
      />
    );
  }

  return (
    <>
      <CartContent
        hasAddOns={addOnSections.length > 0}
        checkoutPaymentMethod={checkoutPaymentMethod}
      />
      {cardSuccessfullyAdded && (
        <VisuallyHidden
          accessibilityLabel={formatMessage({
            id: 'yourNewPaymentMethodHasBeenSuccessfullySaved',
          })}
        />
      )}
    </>
  );
};
